import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  RefreshCw,
  LogOut,
  Volume2,
  VolumeX,
  MapPin,
  Building2,
  History,
  BarChart3,
  QrCode,
  Users,
  Utensils,
  Calendar,
  Package,
  Warehouse,
  PlusCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

import { useStaff } from "@/contexts/StaffContext";
import AdminLogin from "@/components/admin/AdminLogin";
import StaffSelector from "@/components/admin/StaffSelector";
import OrderCard from "@/components/admin/OrderCard";
import AdminStats from "@/components/admin/AdminStats";
import QRPrintCenter from "@/components/admin/QRPrintCenter";
import PredictionWidget from "@/components/admin/PredictionWidget";
import LoyaltyReader from "@/components/admin/LoyaltyReader";
import CashPaymentModal from "@/components/admin/CashPaymentModal";
import PaymentMethodSelector from "@/components/admin/PaymentMethodSelector";
import CardPaymentSuccessModal from "@/components/admin/CardPaymentSuccessModal";
import { TicketPreviewModal } from "@/components/admin/TicketPreviewModal";
import { ExportMenu } from "@/components/admin/ExportMenu";
import StockControl from "@/components/admin/StockControl";
import WarehouseDashboard from "@/pages/admin/WarehouseDashboard";
import ManualOrderModal from "@/components/admin/ManualOrderModal";
import ManualEncargoModal from "@/components/admin/ManualEncargoModal";
import { generateTicketHTML, PrintMode } from "@/lib/printTicket";
import type { Order, OrderStatus, PaymentStatus } from "@/types/orders";
import type { StaffMember } from "@/constants/staff";

type ViewMode = "active" | "history" | "stats" | "qr" | "loyalty" | "stock" | "warehouse";
type ServiceMode = "sala-llevar" | "encargos";
type TabType = "pendientes" | "en-almacen" | "sin-pagar" | "completados";

const NOTIFICATION_SOUND =
  "data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleQ8sntbVl3ImJU+i0cyWbRsbOKLZ1qBkGxQ8n9LPkXgkH0Op2tCPcRkhO5rOypJ8IR88nsrGfn0gHT6Vzr+ChiMjS5jLwoWLIyRHlMy7hYshJUKRzriCiyYoQJDNtYKMJyc+j863go0oJz2Ozb2BiyolPIzMtYKNKiY7i8y0gYwrJjqLzLOAjCsmOorLs4CLKiU6isy0f4srJTuJy7R/iysmO4nLtH+LKyY7icy0f4srJjuJzLR/iywmO4nMtH+LKyY7icy0f4ssJjuJzLR/iywmO4nMtH+LLCc7icy1f4ssJzuJzLV/iywmO4nMtX+LLCY7icy1f4ssJjuJzLV/iywmO4nMtX+LLCY7icy1f4ssJjuJzLV/iywmO4nMtX+LLCY7icy1f4ssJjuJzLV/iywnO4nMtX+LLCc7icy1f4ssJzuJzLV/iywnO4nMtX+LLCc7icy1f4ssJzuJzLV/iywnO4nMtX+LLCc7icy1f4ssJzuJzLV/iywnO4nMtX+LLCc7icy1f4ssJzuJzLV/iywnO4jMtH+LKyc7icy0f4srJzuIzLR/iysnO4jMtH+LKyc6h8y0f4srJzqHzLR/iyonOofMtH+LKic6h8y0f4oqJzqHzLR+iyonOofMtH6LKic6h8y0foopJjqHzLR+iSkmOofMtH6JKiY6h8y0foopJjqHzLR+iSkmOYbMs36JKSY5hsy0fokpJjmGzLN+iSgmOYbMs32JKCc5hsy0fokpJzmGzLR+iSgnOYbMs32JKCY5hsu0fYkoJjmGzLN9iCgmOYbMs32IKSY5hsyzfYgoJjmGzLN9iCkmOIXMsn2IKSY4hcyyfYgpJjmFzLN9iCkmOIXMsn2IKSY4hcyyfYgoJjiFzLJ9iCgmOIXLsn2IKCY4hcuyfYgoJjiFy7J9iCgmOIXLsn2IKCY3hMuxfIcoJzeFy7F9hykpN4XKsXyHKSg3hMqxfIcpJzeFy7F8hykpN4XKsXyGKCo3hMuxfIYoKjaEy7F8higqN4TLsXyGKCo2hMuxfIYoKjaEy7F8higqN4TLsXyGKCo2g8qwfIUoKjaEy7F8hSgqNoTLsXyFKCo2hMuxfIUpKjaEy7F8hSkqNoTLsXyFKSo2g8uxe4UpKjaEy7F8hSkqNoTLsXyFKSo2g8uxe4QoKjaDy7B7hCgqNoTLsXuEKCo2g8uweoQoKjWDy7B7hCgqNYPLsHuEKCo1g8uwe4QoKjWDy7B7hCcqNYPLsHqEJyo1g8uweoQnKjWDy7B6hCcqNYPLsHqEJyo1g8uw";
const ABANDONED_ORDER_THRESHOLD = 30 * 60 * 1000;
// REMOVED: Polling eliminado - ahora usamos solo Supabase Realtime

interface AdminAuthData {
  name: string;
  token: string;
  isSuperAdmin: boolean;
  role: 'admin' | 'warehouse';
  locationId: string | null;
  locationCode: string | null;
  locationName: string | null;
}

interface LocationOption {
  id: string;
  code: string;
  name: string;
}

const Admin = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { currentStaff, setCurrentStaff, clearCurrentStaff } = useStaff();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showStaffSelector, setShowStaffSelector] = useState(false);
  const [authData, setAuthData] = useState<AdminAuthData | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [historyOrders, setHistoryOrders] = useState<Order[]>([]);
  const [serviceMode, setServiceMode] = useState<ServiceMode>("sala-llevar");
  const [activeTab, setActiveTab] = useState<TabType>("pendientes");
  const [viewMode, setViewMode] = useState<ViewMode>("active");
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [locations, setLocations] = useState<LocationOption[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const [cashPaymentOrder, setCashPaymentOrder] = useState<Order | null>(null);
  const [isCashModalOpen, setIsCashModalOpen] = useState(false);
  const [isPaymentSelectorOpen, setIsPaymentSelectorOpen] = useState(false);
  const [isCardSuccessModalOpen, setIsCardSuccessModalOpen] = useState(false);
  const [selectedPaymentOrder, setSelectedPaymentOrder] = useState<Order | null>(null);
  // Ticket preview modal state for iOS-compatible printing
  const [ticketPreviewOpen, setTicketPreviewOpen] = useState(false);
  const [ticketPreviewHtml, setTicketPreviewHtml] = useState("");
  // Manual Order Modal state
  const [isManualOrderOpen, setIsManualOrderOpen] = useState(false);
  const [isManualEncargoOpen, setIsManualEncargoOpen] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const lastOrderCountRef = useRef(0);

  // Filter orders by abandoned threshold
  const visibleOrders = useMemo(() => {
    const now = Date.now();
    return orders.filter((order) => {
      if (order.status === "esperando_pago") {
        const orderTime = new Date(order.created_at).getTime();
        const age = now - orderTime;
        return age < ABANDONED_ORDER_THRESHOLD;
      }
      return true;
    });
  }, [orders]);

  // Filter by service mode (Sala/Llevar vs Encargos) - NO SORTING HERE
  const modeFilteredOrders = useMemo(() => {
    if (serviceMode === "sala-llevar") {
      return visibleOrders.filter(o => o.order_type === "mesa" || o.order_type === "llevar");
    } else {
      return visibleOrders.filter(o => o.order_type === "encargo");
    }
  }, [visibleOrders, serviceMode]);

  // Calculate counts based on new logic
  const pendingCount = useMemo(() => 
    modeFilteredOrders.filter(o => o.status === "pendiente" || o.status === "preparando").length
  , [modeFilteredOrders]);

  // NEW: En Almacén count (only for encargos)
  const enAlmacenCount = useMemo(() => 
    modeFilteredOrders.filter(o => o.status === "en_almacen").length
  , [modeFilteredOrders]);

  // STRICT: Sin Pagar = completado pero NO pagado (estrictamente !== 'pagado')
  const unpaidCount = useMemo(() => 
    modeFilteredOrders.filter(o => 
      o.status === "completado" && 
      o.payment_status !== "pagado"
    ).length
  , [modeFilteredOrders]);

  // STRICT: Completados = completado Y pagado (estrictamente === 'pagado')
  const completedCount = useMemo(() => 
    modeFilteredOrders.filter(o => 
      o.status === "completado" && 
      o.payment_status === "pagado"
    ).length
  , [modeFilteredOrders]);

  // Filter orders by active tab with MIXED SORTING:
  // - Pendientes: FIFO (oldest first - serve who's been waiting longest)
  // - En Almacén: Sorted by pickup_date (soonest first)
  // - Sin Pagar / Completados: LIFO (newest first - see latest transaction first)
  // - Encargos Pendientes: Ordered by pickup_date (soonest first)
  const filteredOrders = useMemo(() => {
    let result;
    switch (activeTab) {
      case "pendientes":
        result = modeFilteredOrders.filter(o => 
          o.status === "pendiente" || o.status === "preparando"
        );
        // For ENCARGOS: Sort by pickup_date (soonest first)
        if (serviceMode === "encargos") {
          return result.sort((a, b) => {
            const dateA = a.pickup_date ? new Date(a.pickup_date).getTime() : Infinity;
            const dateB = b.pickup_date ? new Date(b.pickup_date).getTime() : Infinity;
            return dateA - dateB;
          });
        }
        // For Mesa/Llevar: FIFO (oldest first)
        return result.sort((a, b) => 
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
      case "en-almacen":
        // Encargos en almacén - sorted by pickup_date (soonest first)
        result = modeFilteredOrders.filter(o => o.status === "en_almacen");
        return result.sort((a, b) => {
          const dateA = a.pickup_date ? new Date(a.pickup_date).getTime() : Infinity;
          const dateB = b.pickup_date ? new Date(b.pickup_date).getTime() : Infinity;
          return dateA - dateB;
        });
      case "sin-pagar":
        result = modeFilteredOrders.filter(o => 
          o.status === "completado" && 
          o.payment_status !== "pagado"
        );
        // FIFO: Oldest first (ascending) - serve who's been waiting longest
        return result.sort((a, b) => 
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
      case "completados":
        result = modeFilteredOrders.filter(o => 
          o.status === "completado" && 
          o.payment_status === "pagado"
        );
        // LIFO: Newest first (descending) - see last ticket first
        return result.sort((a, b) => 
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
      default:
        return modeFilteredOrders;
    }
  }, [modeFilteredOrders, activeTab, serviceMode]);

  // Encargos counts (for badge on mode selector)
  const encargosPendingCount = useMemo(() => {
    const encargos = visibleOrders.filter(o => o.order_type === "encargo");
    return encargos.filter(o => o.status === "pendiente" || o.status === "preparando").length;
  }, [visibleOrders]);

  // Encargos en almacén count (for badge on mode selector)
  const encargosEnAlmacenCount = useMemo(() => {
    const encargos = visibleOrders.filter(o => o.order_type === "encargo");
    return encargos.filter(o => o.status === "en_almacen").length;
  }, [visibleOrders]);

  const currentLocationName = useMemo(() => {
    if (!authData) return "";
    if (authData.isSuperAdmin) {
      if (selectedLocationId) {
        const loc = locations.find((l) => l.id === selectedLocationId);
        return loc?.name || "Todas las sedes";
      }
      return "Todas las sedes";
    }
    return authData.locationName || "";
  }, [authData, selectedLocationId, locations]);

  useEffect(() => {
    const verifyToken = async () => {
      const saved = sessionStorage.getItem("adminAuth");
      if (saved) {
        try {
          const savedData = JSON.parse(saved);
          const { data, error } = await supabase.functions.invoke("verify-admin-pin", {
            body: { action: "verify-token", token: savedData.token },
          });

          if (error || !data?.valid) {
            sessionStorage.removeItem("adminAuth");
            return;
          }

          setIsAuthenticated(true);
          setAuthData({
            name: data.name || savedData.name,
            token: savedData.token,
            isSuperAdmin: data.isSuperAdmin || false,
            role: data.role || savedData.role || 'admin',
            locationId: data.locationId || null,
            locationCode: data.locationCode || null,
            locationName: data.locationName || null,
          });
        } catch (err) {
          console.error("Token verification error:", err);
          sessionStorage.removeItem("adminAuth");
        }
      }
    };
    verifyToken();
  }, []);

  // Fetch locations - only superadmin calls API, regular staff uses authData directly
  useEffect(() => {
    if (!isAuthenticated || !authData) return;
    
    // Para empleados normales: usar directamente los datos del token (no hacer API call)
    if (!authData.isSuperAdmin) {
      if (authData.locationId && authData.locationName) {
        setLocations([{
          id: authData.locationId,
          name: authData.locationName,
          code: authData.locationCode || ""
        }]);
      }
      return;
    }
    
    // Solo superadmins hacen la llamada a get-locations
    const fetchLocations = async () => {
      try {
        const { data, error } = await supabase.functions.invoke("admin-orders", {
          body: { action: "get-locations", token: authData.token },
        });
        if (data?.success && data.locations) {
          setLocations(data.locations);
        }
      } catch (error) {
        console.error("Error fetching locations:", error);
      }
    };
    fetchLocations();
  }, [isAuthenticated, authData?.token, authData?.isSuperAdmin, authData?.locationId]);

  const getAdminToken = useCallback(() => authData?.token || null, [authData?.token]);

  // Función de sonido movida arriba para poder usarla en fetchOrders
  const playNotificationSound = useCallback(() => {
    try {
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch((e) => console.log("Audio play blocked", e));
      }
    } catch (e) {
      console.log("Sound error", e);
    }
  }, []);

  // 🔥 OPTIMIZACIÓN: Agregado parámetro skipLoadingState para evitar spinners en updates
  const fetchOrders = useCallback(async (silent = false, skipLoadingState = false) => {
    try {
      // Solo mostrar loading spinner en carga inicial (no en actualizaciones de Realtime)
      if (!silent && !skipLoadingState) {
        setIsLoading(true);
      }

      const token = authData?.token;
      if (!token) return;

      const { data, error } = await supabase.functions.invoke("admin-orders", {
        body: {
          action: "fetch",
          token,
          filterLocationId: authData?.isSuperAdmin ? selectedLocationId : null,
        },
      });

      if (error || !data.success) {
        console.error("❌ Error fetching orders:", data?.error);
        return;
      }

      // CRITICAL MAPPING: order_items → items for frontend components
      const fetchedOrders = (data.orders || []).map((order: any) => ({
        ...order,
        items: order.order_items || order.items || [],
      }));

      // Contar pedidos pendientes ANTES de actualizar estado (para detectar nuevos)
      const newPendingOrders = fetchedOrders.filter(
        (o: Order) => o.status === "pendiente" && o.payment_status !== "pendiente",
      );

      // Solo notificar si hay MÁS pedidos pendientes que antes (evita spam)
      if (
        newPendingOrders.length > lastOrderCountRef.current && 
        lastOrderCountRef.current > 0 && 
        soundEnabled && 
        !silent // Solo en la primera carga manual, no en actualizaciones automáticas
      ) {
        playNotificationSound();
        toast({ title: "🔔 ¡Nuevo pedido!", description: `Pedido recibido` });
      }

      lastOrderCountRef.current = newPendingOrders.length;
      
      // 🔥 ACTUALIZACIÓN ATÓMICA: Evita renders parciales
      setOrders(fetchedOrders);
      
      console.log(`✅ [fetchOrders] ${fetchedOrders.length} pedidos cargados`);
    } catch (error) {
      console.error("❌ Error fetching orders:", error);
    } finally {
      if (!silent && !skipLoadingState) {
        setIsLoading(false);
      }
    }
  }, [authData?.token, authData?.isSuperAdmin, selectedLocationId, soundEnabled, toast, playNotificationSound]);

  // === POLLING SYNC: Actualización periódica cada 3 segundos ===
  // (Realtime deshabilitado porque admin usa anon rol y RLS bloquea SELECT directo)
  useEffect(() => {
    if (!isAuthenticated || !authData?.token) return;

    console.log("🚀 [Polling] Iniciando sincronización cada 3 segundos...");

    // Carga inicial
    fetchOrders();

    // Polling activo cada 3 segundos
    const pollInterval = setInterval(() => {
      console.log("🔄 [Polling] Auto-refresh");
      fetchOrders(true); // silent = true para no mostrar spinner
    }, 3000);

    return () => {
      console.log("🔌 [Polling] Detenido");
      clearInterval(pollInterval);
    };
  }, [isAuthenticated, authData?.token, fetchOrders]);

  // Refrescar al volver a la pestaña
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && isAuthenticated) {
        console.log("👁️ [Visibility] Ventana visible - refrescando pedidos");
        fetchOrders(true);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [isAuthenticated, fetchOrders]);



  // Reset tab to "pendientes" when switching modes if current tab is invalid
  // "sin-pagar" is now valid for BOTH modes
  useEffect(() => {
    if (serviceMode === "encargos" && activeTab === "en-almacen") {
      // en-almacen is valid for encargos, keep it
    }
  }, [serviceMode, activeTab]);

  const handleLogout = () => {
    sessionStorage.removeItem("adminAuth");
    clearCurrentStaff();
    setIsAuthenticated(false);
    setAuthData(null);
    setShowStaffSelector(false);
    navigate("/");
  };

  // Reset to login screen (stays on /admin, shows PIN input)
  const handleResetToLogin = () => {
    sessionStorage.removeItem("adminAuth");
    clearCurrentStaff();
    setAuthData(null);
    setIsAuthenticated(false);
    setShowStaffSelector(false);
    // No navigate - component will render AdminLogin automatically
  };

  // Staff logout - returns to staff selector without requiring PIN again
  const handleStaffLogout = () => {
    clearCurrentStaff();
    setShowStaffSelector(true);
    toast({ title: "Sesión de empleado cerrada", description: "Selecciona tu usuario para continuar" });
  };

  // Handle staff selection after PIN verification
  const handleStaffSelected = (staff: StaffMember) => {
    setCurrentStaff(staff);
    setShowStaffSelector(false);
    toast({ title: `¡Bienvenido, ${staff.name}!` });
  };

  const handleStatusChange = async (orderId: string, newStatus: OrderStatus) => {
    setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, status: newStatus } : o)));
    try {
      const token = getAdminToken();
      if (!token) return;
      await supabase.functions.invoke("admin-orders", {
        body: { action: "update-status", token, orderId, newStatus },
      });
      toast({ title: "Estado actualizado" });
    } catch (error) {
      fetchOrders();
      toast({ title: "Error", variant: "destructive" });
    }
  };

  // Open payment method selector modal
  const handleOpenPaymentSelector = (orderId: string) => {
    const order = orders.find(o => o.id === orderId);
    if (order) {
      setSelectedPaymentOrder(order);
      setIsPaymentSelectorOpen(true);
    }
  };

  // Handle cash payment selection
  const handleSelectCashPayment = () => {
    if (selectedPaymentOrder) {
      setCashPaymentOrder(selectedPaymentOrder);
      setIsPaymentSelectorOpen(false);
      setIsCashModalOpen(true);
    }
  };

  // Handle card payment selection and process
  const handleSelectCardPayment = async () => {
    if (!selectedPaymentOrder) return;

    // Close selector
    setIsPaymentSelectorOpen(false);

    try {
      const token = getAdminToken();
      if (!token) return;

      const { data, error } = await supabase.functions.invoke("admin-orders", {
        body: {
          action: "mark-as-paid",
          token,
          orderId: selectedPaymentOrder.id,
          paymentMethod: "card",
          servedBy: currentStaff?.name,
        },
      });

      if (error) {
        throw new Error(error.message || "Error al conectar con el servidor");
      }
      if (!data?.success) {
        if (data?.alreadyPaid) {
          toast({
            title: "Pedido ya cobrado",
            description: "Este pedido ya ha sido cobrado anteriormente.",
          });
          fetchOrders();
          setSelectedPaymentOrder(null);
          return;
        }
        throw new Error(data?.error || "Error desconocido al procesar el pago");
      }

      // Éxito: actualización optimista
      const optimisticOrder: Order = {
        ...selectedPaymentOrder,
        payment_status: "pagado" as PaymentStatus,
        payment_method: "local" as const,
        served_by: currentStaff?.name,
        updated_at: new Date().toISOString(),
      };

      setSelectedPaymentOrder(optimisticOrder);
      setOrders((prev) =>
        prev.map((o) => (o.id === selectedPaymentOrder.id ? optimisticOrder : o)),
      );

      setIsCardSuccessModalOpen(true);
      toast({ title: "💳 Cobrado con tarjeta" });
    } catch (error: any) {
      console.error("[Card Payment] Error:", error);
      setIsPaymentSelectorOpen(true);
      toast({
        title: "Error al procesar el pago",
        description: error.message || "Intenta de nuevo",
        variant: "destructive",
      });
      fetchOrders();
    }
  };

  // Handle card success modal print - uses optimistic selectedPaymentOrder
  const handleCardSuccessPrint = () => {
    if (selectedPaymentOrder) {
      // Use the already-updated selectedPaymentOrder (contains payment_status: 'pagado')
      handleGlobalPrint(selectedPaymentOrder);
    }
    setIsCardSuccessModalOpen(false);
    setSelectedPaymentOrder(null);
  };

  const handleCardSuccessClose = () => {
    setIsCardSuccessModalOpen(false);
    setSelectedPaymentOrder(null);
  };

  // Confirm cash payment with TPV data
  const handleConfirmCashPayment = async (orderId: string, cashReceived: number, changeReturned: number) => {
    const currentOrder = orders.find(o => o.id === orderId) || cashPaymentOrder;

    if (!currentOrder) {
      toast({ title: "Error", description: "Pedido no encontrado", variant: "destructive" });
      return;
    }

    try {
      const token = getAdminToken();
      if (!token) return;

      const { data, error } = await supabase.functions.invoke("admin-orders", {
        body: {
          action: "mark-as-paid",
          token,
          orderId,
          cashReceived,
          changeReturned,
          paymentMethod: "cash",
          servedBy: currentStaff?.name,
        },
      });

      if (error) {
        throw new Error(error.message || "Error al conectar con el servidor");
      }
      if (!data?.success) {
        if (data?.alreadyPaid) {
          toast({
            title: "Pedido ya cobrado",
            description: "Este pedido ya ha sido cobrado anteriormente. Refrescando...",
          });
          fetchOrders();
          setIsCashModalOpen(false);
          setCashPaymentOrder(null);
          return;
        }
        throw new Error(data?.error || "Error desconocido al procesar el pago");
      }

      // Éxito: actualización optimista
      const optimisticOrder: Order = {
        ...currentOrder,
        payment_status: "pagado" as PaymentStatus,
        cash_received: cashReceived,
        change_returned: changeReturned,
        served_by: currentStaff?.name,
        updated_at: new Date().toISOString(),
      };

      setOrders((prev) => prev.map((o) => (o.id === orderId ? optimisticOrder : o)));
      setCashPaymentOrder(optimisticOrder);

      toast({ title: "💰 Cobrado", description: `Cambio: ${changeReturned.toFixed(2)}€` });
    } catch (error: any) {
      console.error("[Cash Payment] Error:", error);
      toast({
        title: "Error al procesar el pago",
        description: error.message || "Intenta de nuevo",
        variant: "destructive",
      });
      fetchOrders();
    }
  };

  // Global print handler - uses preview modal for iOS/Safari compatibility
  // Supports both "ticket" (paid) and "cuenta" (unpaid) modes
  // ALWAYS fetch fresh data to ensure items and served_by are up-to-date
  const handleGlobalPrint = useCallback(async (orderInput: Order, mode: PrintMode = "ticket") => {
    let orderToPrintData = orderInput;
    
    // ALWAYS fetch fresh data from backend to ensure complete order info
    console.log("[Admin] Fetching fresh order data for printing...", orderInput.id, "mode:", mode);
    try {
      const token = getAdminToken();
      if (token) {
        const { data } = await supabase.functions.invoke("admin-orders", {
          body: { action: "fetch-one", token, orderId: orderInput.id },
        });
        if (data?.success && data.order) {
          orderToPrintData = {
            ...data.order,
            items: data.order.order_items || data.order.items || [],
          };
          console.log("[Admin] Fresh order data fetched:", {
            items: orderToPrintData.items?.length,
            served_by: orderToPrintData.served_by,
            payment_status: orderToPrintData.payment_status,
            ticket_series: orderToPrintData.ticket_series,
            series_number: orderToPrintData.series_number
          });
        } else {
          console.warn("[Admin] Could not fetch fresh data, using provided order");
        }
      }
    } catch (err) {
      console.error("[Admin] Error fetching fresh order:", err);
      // Fall back to provided order data
    }
    
    // Generate HTML based on mode and open preview modal for iOS compatibility
    try {
      const html = generateTicketHTML(orderToPrintData as any, mode);
      setTicketPreviewHtml(html);
      setTicketPreviewOpen(true);
      console.log("[Admin] Ticket preview modal opened, mode:", mode);
    } catch (printErr) {
      console.error("[Admin] Error generating ticket:", printErr);
      toast({ 
        title: "Error de impresión", 
        description: "No se pudo generar el ticket. Intente de nuevo.", 
        variant: "destructive" 
      });
    }
  }, [toast, getAdminToken]);

  // Handler for printing Ticket (paid receipt)
  const handlePrintTicket = useCallback((order: Order) => {
    handleGlobalPrint(order, "ticket");
  }, [handleGlobalPrint]);

  // Handler for printing Cuenta (unpaid bill)
  const handlePrintCuenta = useCallback((order: Order) => {
    handleGlobalPrint(order, "cuenta");
  }, [handleGlobalPrint]);

  // Listen for print-receipt event from CashPaymentModal
  useEffect(() => {
    const handlePrintReceipt = (e: CustomEvent<string>) => {
      const orderId = e.detail;
      const order = orders.find(o => o.id === orderId);
      if (order) {
        handleGlobalPrint(order);
      } else {
        console.warn("Order not found for printing:", orderId);
      }
    };
    
    window.addEventListener('print-receipt', handlePrintReceipt as EventListener);
    return () => window.removeEventListener('print-receipt', handlePrintReceipt as EventListener);
  }, [orders, handleGlobalPrint]);

  const fetchHistoryOrders = async () => {
    try {
      setIsLoadingHistory(true);
      const token = getAdminToken();
      if (!token) return;
      const { data } = await supabase.functions.invoke("admin-orders", {
        body: {
          action: "fetch-history",
          token,
          filterLocationId: authData?.isSuperAdmin ? selectedLocationId : null,
        },
      });
      if (data?.success) {
        // CRITICAL MAPPING: order_items → items for frontend components
        const mappedOrders = (data.orders || []).map((order: any) => ({
          ...order,
          items: order.order_items || order.items || [],
        }));
        setHistoryOrders(mappedOrders);
      }
    } finally {
      setIsLoadingHistory(false);
    }
  };

  useEffect(() => {
    if (viewMode === "history" && isAuthenticated) fetchHistoryOrders();
  }, [viewMode, selectedLocationId]);

  // Show AdminLogin if not authenticated
  if (!isAuthenticated) {
    return (
      <AdminLogin
        onSuccess={(data) => {
          setIsAuthenticated(true);
          setAuthData(data);
          sessionStorage.setItem("adminAuth", JSON.stringify(data));
          // If no staff selected, show staff selector (unless warehouse role)
          if (!currentStaff && data.role !== 'warehouse') {
            setShowStaffSelector(true);
          }
        }}
      />
    );
  }

  // If role is 'warehouse', go directly to WarehouseDashboard (skip staff selector)
  if (authData?.role === 'warehouse') {
    return (
      <WarehouseDashboard 
        token={authData.token} 
        onBack={() => {
          sessionStorage.removeItem("adminAuth");
          setIsAuthenticated(false);
          setAuthData(null);
        }} 
      />
    );
  }

  // Show StaffSelector if authenticated but no staff selected
  // Pass isSuperAdmin to filter staff list (only show Rubén for superadmin)
  if (showStaffSelector || (!currentStaff && isAuthenticated)) {
    return (
      <StaffSelector 
        onSelectStaff={handleStaffSelected} 
        isSuperAdmin={authData?.isSuperAdmin}
        onBack={handleResetToLogin}
      />
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <audio ref={audioRef} src={NOTIFICATION_SOUND} preload="auto" />

      <header className="sticky top-0 z-50 bg-primary text-primary-foreground py-3 px-4 shadow-md">
        <div className="container mx-auto">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              <h1 className="font-serif text-lg font-bold">Panel Admin</h1>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="icon"
                variant="ghost"
                onClick={() => setSoundEnabled(!soundEnabled)}
                className="text-primary-foreground hover:bg-primary-foreground/10"
              >
                {soundEnabled ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
              </Button>

              <Button
                size="sm"
                onClick={() => setIsManualOrderOpen(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold"
              >
                <PlusCircle className="h-4 w-4 mr-1" />
                <span className="hidden sm:inline">Nuevo Pedido</span>
                <span className="sm:hidden">+</span>
              </Button>
              <Button
                size="icon"
                variant="ghost"
                onClick={() => fetchOrders()}
                className="text-primary-foreground hover:bg-primary-foreground/10"
              >
                <RefreshCw className="h-5 w-5" />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                onClick={handleLogout}
                className="text-primary-foreground hover:bg-primary-foreground/10"
              >
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>

          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              <span className="font-medium">{currentLocationName}</span>
              <Badge variant="outline" className="border-primary-foreground/30 text-primary-foreground text-xs">
                {authData?.name}
              </Badge>
              {/* Staff Badge with logout */}
              {currentStaff && (
                <button
                  onClick={handleStaffLogout}
                  className="flex items-center gap-2 bg-primary-foreground/10 hover:bg-primary-foreground/20 px-3 py-1 rounded-full transition-colors cursor-pointer"
                  title="Cambiar de empleado"
                >
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
                    style={{ backgroundColor: currentStaff.color }}
                  >
                    {currentStaff.initials}
                  </div>
                  <span className="text-sm font-medium hidden sm:inline">{currentStaff.name}</span>
                  <LogOut className="h-3 w-3 opacity-60" />
                </button>
              )}
            </div>

            {authData?.isSuperAdmin && locations.length > 0 && (
              <Select
                value={selectedLocationId || "all"}
                onValueChange={(v) => setSelectedLocationId(v === "all" ? null : v)}
              >
                <SelectTrigger className="w-[180px] h-8 bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground">
                  <SelectValue placeholder="Filtrar por sede" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las sedes</SelectItem>
                  {locations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>
                      {loc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
      </header>

      {/* Service Mode Selector - Sala/Llevar vs Encargos */}
      <div className="container mx-auto px-4 py-4">
        <div className="grid grid-cols-2 gap-3 mb-4">
          <Button
            variant={serviceMode === "sala-llevar" ? "default" : "outline"}
            onClick={() => setServiceMode("sala-llevar")}
            className={`h-14 text-base relative ${serviceMode === "sala-llevar" ? "bg-primary" : ""}`}
          >
            <Utensils className="h-5 w-5 mr-2" />
            <span>🍽️ Sala y Llevar</span>
            {/* Badge shows pending Sala/Llevar orders when NOT in this mode */}
            {serviceMode !== "sala-llevar" && (() => {
              const salaLlevarPending = orders.filter(
                o => (o.order_type === "mesa" || o.order_type === "llevar") && 
                     (o.status === "pendiente" || o.status === "preparando")
              ).length;
              return salaLlevarPending > 0 && (
                <Badge className="absolute -top-2 -right-2 bg-red-500 text-white">
                  {salaLlevarPending}
                </Badge>
              );
            })()}
          </Button>
          <Button
            variant={serviceMode === "encargos" ? "default" : "outline"}
            onClick={() => setServiceMode("encargos")}
            className={`h-14 text-base relative ${serviceMode === "encargos" ? "bg-orange-600 hover:bg-orange-700" : "bg-orange-50 border-orange-300 text-orange-700 hover:bg-orange-100"}`}
          >
            <Calendar className="h-5 w-5 mr-2" />
            <span>📅 Encargos</span>
            {/* Badge shows pending Encargos when NOT in this mode */}
            {serviceMode !== "encargos" && (() => {
              const encargosPending = orders.filter(
                o => o.order_type === "encargo" && 
                     (o.status === "pendiente" || o.status === "preparando")
              ).length;
              return encargosPending > 0 && (
                <Badge className="absolute -top-2 -right-2 bg-orange-500 text-white">
                  {encargosPending}
                </Badge>
              );
            })()}
          </Button>
        </div>

        {/* Crear Encargo Button - Only visible in encargos mode */}
        {serviceMode === "encargos" && (
          <Button
            onClick={() => setIsManualEncargoOpen(true)}
            className="w-full h-12 bg-purple-600 hover:bg-purple-700 text-white font-semibold text-base"
          >
            <PlusCircle className="h-5 w-5 mr-2" />
            Crear Encargo Manual
          </Button>
        )}

        {/* Stats Cards - Dynamic based on service mode */}
        <div className={`grid gap-3 mt-4 ${serviceMode === "encargos" ? "grid-cols-4" : "grid-cols-3"}`}>
          <Card
            className={`cursor-pointer transition-all ${activeTab === "pendientes" ? "ring-2 ring-red-500" : ""}`}
            onClick={() => setActiveTab("pendientes")}
          >
            <CardContent className="p-3 text-center">
              <div className="text-2xl font-bold text-red-600">{pendingCount}</div>
              <p className="text-xs text-muted-foreground">🔴 Pendientes</p>
            </CardContent>
          </Card>

          {serviceMode === "encargos" && (
            <Card
              className={`cursor-pointer transition-all ${activeTab === "en-almacen" ? "ring-2 ring-purple-500" : ""}`}
              onClick={() => setActiveTab("en-almacen")}
            >
              <CardContent className="p-3 text-center">
                <div className="text-2xl font-bold text-purple-600">{enAlmacenCount}</div>
                <p className="text-xs text-muted-foreground">📦 En Almacén</p>
              </CardContent>
            </Card>
          )}

          {/* Sin Pagar - Now visible in BOTH modes */}
          <Card
            className={`cursor-pointer transition-all ${activeTab === "sin-pagar" ? "ring-2 ring-orange-500" : ""}`}
            onClick={() => setActiveTab("sin-pagar")}
          >
            <CardContent className="p-3 text-center">
              <div className="text-2xl font-bold text-orange-600">{unpaidCount}</div>
              <p className="text-xs text-muted-foreground">
                {serviceMode === "encargos" ? "💸 Pendiente Pago" : "🟠 Sin Pagar"}
              </p>
            </CardContent>
          </Card>

          <Card
            className={`cursor-pointer transition-all ${activeTab === "completados" ? "ring-2 ring-green-500" : ""}`}
            onClick={() => setActiveTab("completados")}
          >
            <CardContent className="p-3 text-center">
              <div className="text-2xl font-bold text-green-600">{completedCount}</div>
              <p className="text-xs text-muted-foreground">✅ Completados</p>
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="container mx-auto px-4 py-2">
        <div className="grid grid-cols-2 sm:flex gap-2">
          <Button
            variant={viewMode === "active" ? "default" : "outline"}
            onClick={() => setViewMode("active")}
            className="flex-1 min-w-0"
          >
            <span className="truncate">Pedidos Activos</span>
          </Button>
          <Button
            variant={viewMode === "history" ? "default" : "outline"}
            onClick={() => setViewMode("history")}
            className="flex-1 min-w-0"
          >
            <History className="h-4 w-4 mr-1 shrink-0" />
            <span className="truncate">Historial</span>
          </Button>
          <Button
            variant={viewMode === "loyalty" ? "default" : "outline"}
            onClick={() => setViewMode("loyalty")}
            className="flex-1 min-w-0 bg-secondary/10 border-secondary text-secondary hover:bg-secondary hover:text-secondary-foreground"
          >
            <Users className="h-4 w-4 mr-1 shrink-0" />
            <span className="truncate">Lector Socio</span>
          </Button>
          <Button
            variant={viewMode === "stock" ? "default" : "outline"}
            onClick={() => setViewMode("stock")}
            className="flex-1 min-w-0"
          >
            <Package className="h-4 w-4 mr-1 shrink-0" />
            <span className="truncate">Stock</span>
          </Button>
          {authData?.isSuperAdmin && (
            <>
              <Button
                variant={viewMode === "stats" ? "default" : "outline"}
                onClick={() => setViewMode("stats")}
                className="flex-1 min-w-0"
              >
                <BarChart3 className="h-4 w-4 mr-1 shrink-0" />
                <span className="truncate">Estadísticas</span>
              </Button>
              <Button
                variant={viewMode === "qr" ? "default" : "outline"}
                onClick={() => setViewMode("qr")}
                className="flex-1 min-w-0"
              >
                <QrCode className="h-4 w-4 mr-1 shrink-0" />
                <span className="truncate">Imprimir QR</span>
              </Button>
              <Button
                variant={viewMode === "warehouse" ? "default" : "outline"}
                onClick={() => setViewMode("warehouse")}
                className="flex-1 min-w-0 bg-purple-50 border-purple-300 text-purple-700 hover:bg-purple-100"
              >
                <Warehouse className="h-4 w-4 mr-1 shrink-0" />
                <span className="truncate">🏭 Almacén</span>
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="container mx-auto px-4 pb-8">
        {viewMode === "active" ? (
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabType)}>
            <TabsList className={`w-full mb-4 grid ${serviceMode === "encargos" ? "grid-cols-4" : "grid-cols-3"}`}>
              <TabsTrigger value="pendientes" className="relative">
                🔴 Pendientes
                {pendingCount > 0 && (
                  <Badge className="absolute -top-2 -right-2 bg-red-500 text-white text-xs px-1.5">
                    {pendingCount}
                  </Badge>
                )}
              </TabsTrigger>
              {serviceMode === "encargos" && (
                <TabsTrigger value="en-almacen" className="relative">
                  📦 Almacén
                  {enAlmacenCount > 0 && (
                    <Badge className="absolute -top-2 -right-2 bg-purple-500 text-white text-xs px-1.5">
                      {enAlmacenCount}
                    </Badge>
                  )}
                </TabsTrigger>
              )}
              {/* Sin Pagar - Now visible in BOTH modes */}
              <TabsTrigger value="sin-pagar" className="relative">
                💸 {serviceMode === "encargos" ? "Por Cobrar" : "Sin Pagar"}
                {unpaidCount > 0 && (
                  <Badge className="absolute -top-2 -right-2 bg-orange-500 text-white text-xs px-1.5">
                    {unpaidCount}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="completados" className="relative">
                ✅ Completados
                {completedCount > 0 && (
                  <Badge className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-1.5">
                    {completedCount}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>

            <TabsContent value={activeTab} className="mt-0">
              {isLoading ? (
                <div className="text-center py-12 text-muted-foreground">Cargando pedidos...</div>
              ) : filteredOrders.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  {activeTab === "pendientes" && "No hay pedidos pendientes"}
                  {activeTab === "en-almacen" && "No hay encargos en almacén"}
                  {activeTab === "sin-pagar" && "No hay pedidos sin pagar"}
                  {activeTab === "completados" && "No hay pedidos completados"}
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredOrders.map((order) => (
                    <OrderCard
                      key={order.id}
                      order={order}
                      onStatusChange={handleStatusChange}
                      onMarkAsPaid={handleOpenPaymentSelector}
                      onPrintTicket={handlePrintTicket}
                      onPrintCuenta={handlePrintCuenta}
                      showLocation={authData?.isSuperAdmin && !selectedLocationId}
                      isEncargoMode={serviceMode === "encargos"}
                    />
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        ) : viewMode === "history" ? (
          <div className="mt-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <History className="h-5 w-5" /> Historial de pedidos
              </h2>
              <div className="flex flex-wrap gap-2">
                {authData && (
                  <ExportMenu 
                    token={authData.token} 
                    isSuperAdmin={authData.isSuperAdmin} 
                    selectedLocationId={selectedLocationId}
                    locations={locations}
                  />
                )}
                <Button size="sm" variant="outline" onClick={() => fetchHistoryOrders()} disabled={isLoadingHistory}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${isLoadingHistory ? "animate-spin" : ""}`} /> Actualizar
                </Button>
              </div>
            </div>
            {isLoadingHistory ? (
              <div className="text-center py-12 text-muted-foreground">Cargando historial...</div>
            ) : historyOrders.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">No hay pedidos en el historial</div>
            ) : (
              <div className="space-y-4">
                {historyOrders.map((order) => (
                  <OrderCard
                    key={order.id}
                    order={order}
                    onStatusChange={handleStatusChange}
                    onMarkAsPaid={handleOpenPaymentSelector}
                    onPrintTicket={handlePrintTicket}
                    onPrintCuenta={handlePrintCuenta}
                    showLocation={authData?.isSuperAdmin && !selectedLocationId}
                  />
                ))}
              </div>
            )}
          </div>
        ) : viewMode === "stats" && authData?.isSuperAdmin ? (
          <div className="mt-4 space-y-6">
            {/* Prediction Widget at the top */}
            <PredictionWidget token={authData.token} />
            {/* Stats Dashboard */}
            <AdminStats token={authData.token} selectedLocationId={selectedLocationId} isSuperAdmin={authData.isSuperAdmin} />
          </div>
        ) : viewMode === "qr" && authData?.isSuperAdmin ? (
          <div className="mt-4">
            <QRPrintCenter token={authData.token} />
          </div>
        ) : viewMode === "loyalty" && authData ? (
          <div className="mt-4">
            <LoyaltyReader staffPinId={authData.token} locationId={authData.locationId || ""} />
          </div>
        ) : viewMode === "stock" && authData ? (
          <div className="mt-4">
            <StockControl 
              locationId={authData.isSuperAdmin && selectedLocationId ? selectedLocationId : (authData.locationId || "")}
              locationName={authData.isSuperAdmin && selectedLocationId 
                ? (locations.find(l => l.id === selectedLocationId)?.name || "Sede")
                : (authData.locationName || "Sede")
              }
              token={authData.token}
            />
          </div>
        ) : viewMode === "warehouse" && authData?.isSuperAdmin ? (
          <WarehouseDashboard 
            token={authData.token} 
            onBack={() => setViewMode("active")} 
          />
        ) : null}
      </div>

      {/* Payment Method Selector Modal */}
      <PaymentMethodSelector
        isOpen={isPaymentSelectorOpen}
        onClose={() => {
          setIsPaymentSelectorOpen(false);
          setSelectedPaymentOrder(null);
        }}
        onSelectCash={handleSelectCashPayment}
        onSelectCard={handleSelectCardPayment}
        orderTotal={selectedPaymentOrder?.total || 0}
        orderNumber={selectedPaymentOrder?.order_number}
      />

      {/* Cash Payment Modal (TPV) */}
      <CashPaymentModal
        isOpen={isCashModalOpen}
        onClose={() => {
          setIsCashModalOpen(false);
          setCashPaymentOrder(null);
          setSelectedPaymentOrder(null);
        }}
        onBack={() => {
          // Volver al selector de método de pago
          setIsCashModalOpen(false);
          setIsPaymentSelectorOpen(true);
        }}
        order={cashPaymentOrder}
        onConfirmPayment={handleConfirmCashPayment}
      />

      {/* Card Payment Success Modal */}
      <CardPaymentSuccessModal
        isOpen={isCardSuccessModalOpen}
        onClose={handleCardSuccessClose}
        onPrint={handleCardSuccessPrint}
        order={selectedPaymentOrder}
      />

      {/* Ticket Preview Modal for iOS-compatible printing */}
      <TicketPreviewModal
        isOpen={ticketPreviewOpen}
        onClose={() => setTicketPreviewOpen(false)}
        ticketHtml={ticketPreviewHtml}
      />

      <ManualOrderModal
        isOpen={isManualOrderOpen}
        onClose={() => setIsManualOrderOpen(false)}
        locationId={authData?.isSuperAdmin ? (selectedLocationId || authData?.locationId || "") : (authData?.locationId || "")}
        currentStaffName={currentStaff?.name || "Empleado"}
        onOrderCreated={fetchOrders}
        token={authData?.token || ""}
      />

      {/* Manual Encargo Modal */}
      <ManualEncargoModal
        isOpen={isManualEncargoOpen}
        onClose={() => setIsManualEncargoOpen(false)}
        locationId={authData?.isSuperAdmin ? (selectedLocationId || authData?.locationId || "") : (authData?.locationId || "")}
        currentStaffName={currentStaff?.name || "Empleado"}
        onOrderCreated={fetchOrders}
        token={authData?.token || ""}
      />
    </div>
  );
};

export default Admin;
